import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Send, CheckCircle, AlertCircle, RefreshCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import AdminBreadcrumb from '@/components/AdminBreadcrumb';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  recipient_email: '',
  recipient_name: '',
  custom_text: '',
  order_id: '',
  discount_code: '',
};

export default function EmailsAdminPage() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  const fetchStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/emails/status`);
      setStatus(data);
    } catch {
      setStatus(null);
    }
  };
  const fetchLog = async () => {
    try {
      setLogLoading(true);
      const { data } = await axios.get(`${API}/emails/log`, { params: { limit: 30 } });
      setLog(Array.isArray(data) ? data : []);
    } catch {
      setLog([]);
    } finally {
      setLogLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchLog();
  }, []);

  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const handleSend = async (e) => {
    e?.preventDefault?.();
    if (!form.recipient_email || !form.recipient_email.includes('@')) {
      toast.error('Enter a valid recipient email');
      return;
    }
    try {
      setSending(true);
      const payload = {
        recipient_email: form.recipient_email.trim(),
        recipient_name: form.recipient_name.trim() || null,
        custom_text: form.custom_text.trim() || null,
        order_id: form.order_id.trim() || null,
        discount_code: form.discount_code.trim() || null,
      };
      const { data } = await axios.post(`${API}/emails/review-request`, payload);
      if (data?.success) {
        toast.success(`Review email sent to ${payload.recipient_email}`);
        setForm(EMPTY);
        fetchLog();
      } else {
        toast.error('Email did not send — check backend logs.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to send email';
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const configured = !!status?.resend_configured;

  return (
    <div className="pt-28 pb-16 min-h-screen" data-testid="emails-admin-page">
      <div className="container-custom px-6 md:px-12 lg:px-24 max-w-5xl mx-auto">
        <AdminBreadcrumb current="Emails" />
        <div className="flex items-start justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="text-caption text-[#84A98C] mb-2">Admin · Emails</p>
            <h1 className="heading-display text-3xl md:text-4xl text-[#354F52]">Review-request Emails</h1>
            <p className="text-[#52796F] mt-3 max-w-xl">
              Send a hand-warm review email to a customer 7–10 days after their order arrived.
              Optionally include a thank-you discount code for their next purchase.
            </p>
          </div>
        </div>

        {/* Status */}
        <div
          className={`p-5 rounded-sm mb-8 flex items-start gap-3 ${
            configured ? 'bg-[#52796F]/10 border border-[#52796F]/40' : 'bg-[#BC4749]/10 border border-[#BC4749]/40'
          }`}
          data-testid="emails-status-card"
        >
          {configured ? (
            <CheckCircle className="w-5 h-5 text-[#52796F] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          ) : (
            <AlertCircle className="w-5 h-5 text-[#BC4749] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          )}
          <div className="flex-1 text-sm text-[#354F52]">
            {configured ? (
              <>
                <p className="font-medium">Resend is configured.</p>
                <p className="text-[#52796F] text-xs mt-1">
                  Sending from <span className="font-medium text-[#354F52]">{status?.sender_email}</span>
                  {status?.bcc_owner_configured ? ' · A copy is BCC\u2019d to the store owner.' : ' · No BCC owner set.'}
                </p>
              </>
            ) : (
              <>
                <p className="font-medium">Resend is not yet configured — sending is disabled.</p>
                <p className="text-[#52796F] text-xs mt-1">
                  Add <code className="bg-[#FAF9F6] px-1 py-0.5 rounded">RESEND_API_KEY</code>,{' '}
                  <code className="bg-[#FAF9F6] px-1 py-0.5 rounded">SENDER_EMAIL</code> (a verified Resend
                  domain), and optionally <code className="bg-[#FAF9F6] px-1 py-0.5 rounded">STORE_OWNER_EMAIL</code> to
                  <code className="bg-[#FAF9F6] px-1 py-0.5 rounded ml-1">/app/backend/.env</code>, then restart the backend.
                </p>
              </>
            )}
          </div>
          <button
            type="button"
            onClick={fetchStatus}
            className="text-xs text-[#52796F] hover:text-[#354F52] inline-flex items-center gap-1"
            data-testid="emails-status-refresh"
          >
            <RefreshCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-8">
          {/* Form */}
          <form onSubmit={handleSend} className="space-y-5 bg-[#F5F5F0] p-7 rounded-sm" data-testid="email-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="recipient_email" className="text-[#354F52]">Recipient email *</Label>
                <Input
                  id="recipient_email"
                  type="email"
                  value={form.recipient_email}
                  onChange={set('recipient_email')}
                  required
                  className="border-[#CAD2C5]"
                  data-testid="email-to"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="recipient_name" className="text-[#354F52]">First name</Label>
                <Input
                  id="recipient_name"
                  value={form.recipient_name}
                  onChange={set('recipient_name')}
                  className="border-[#CAD2C5]"
                  data-testid="email-name"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="custom_text" className="text-[#354F52]">Bracelet word</Label>
                <Input
                  id="custom_text"
                  value={form.custom_text}
                  onChange={set('custom_text')}
                  placeholder="e.g. LOVE"
                  className="uppercase tracking-widest border-[#CAD2C5]"
                  data-testid="email-bracelet-text"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="order_id" className="text-[#354F52]">Order ID (optional)</Label>
                <Input
                  id="order_id"
                  value={form.order_id}
                  onChange={set('order_id')}
                  className="border-[#CAD2C5]"
                  data-testid="email-order-id"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="discount_code" className="text-[#354F52]">
                Thank-you discount code (optional)
              </Label>
              <Input
                id="discount_code"
                value={form.discount_code}
                onChange={(e) => set('discount_code')({ target: { value: e.target.value.toUpperCase().replace(/\s+/g, '') } })}
                placeholder="e.g. THANKS10"
                className="uppercase tracking-widest border-[#CAD2C5]"
                data-testid="email-discount"
              />
              <p className="text-xs text-[#84A98C]">Shown as a thank-you in the email body.</p>
            </div>

            <Button
              type="submit"
              disabled={sending || !configured}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="email-send"
            >
              {sending ? (
                <>Sending…</>
              ) : (
                <>
                  <Send className="w-4 h-4" strokeWidth={1.5} />
                  Send review email
                </>
              )}
            </Button>
            {!configured && (
              <p className="text-xs text-[#BC4749] text-center">
                Sending disabled until <code>RESEND_API_KEY</code> is set.
              </p>
            )}
          </form>

          {/* Recent log */}
          <div className="bg-[#FAF9F6] p-7 rounded-sm border border-[#CAD2C5]/40">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#84A98C]" strokeWidth={1.5} />
                <h2 className="font-serif text-lg text-[#354F52]">Recent sends</h2>
              </div>
              <button
                type="button"
                onClick={fetchLog}
                className="text-xs text-[#52796F] hover:text-[#354F52] inline-flex items-center gap-1"
                data-testid="emails-log-refresh"
              >
                <RefreshCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
                Refresh
              </button>
            </div>

            {logLoading ? (
              <p className="text-sm text-[#84A98C]">Loading…</p>
            ) : log.length === 0 ? (
              <p className="text-sm text-[#84A98C]">No emails sent yet.</p>
            ) : (
              <ul className="divide-y divide-[#CAD2C5]/40" data-testid="emails-log-list">
                {log.map((row, idx) => (
                  <li key={row.id || idx} className="py-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-[#354F52] truncate">{row.recipient_email}</p>
                      <p className="text-xs text-[#84A98C]">
                        {row.kind || 'email'}
                        {row.order_id ? ` · order ${row.order_id}` : ''}
                      </p>
                    </div>
                    <p className="text-xs text-[#84A98C] whitespace-nowrap">
                      {row.sent_at ? new Date(row.sent_at).toLocaleString('en-GB') : ''}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
